import { Zap, Crown, Building2, type LucideIcon } from "lucide-react";

export type FeatureValue = boolean | string;

// Translation key under landing.pricing.features.* — components render with t().
export type PlanFeatureKey =
  | "workerApp"
  | "customerApp"
  | "invoicing"
  | "scheduling"
  | "gps"
  | "routeHistory"
  | "equipment"
  | "googleCalendar"
  | "performance"
  | "multiLocation"
  | "analytics"
  | "api"
  | "audit";

export interface PlanFeature {
  key: PlanFeatureKey;
  starter: boolean;
  professional: boolean;
  business: boolean;
  /** Included in the plan(s) above but not shipped yet — render as "Coming soon". */
  comingSoon?: boolean;
}

// Translation key under landing.compare.rows.* — value labels (Basic, Pro only)
// resolve through landing.compare.values.* via the `valueKey` helper.
export type CompetitorRowKey =
  | "workerApp"
  | "customerApp"
  | "invoicing"
  | "realtimeGps"
  | "routeHistory"
  | "bilingual"
  | "noContract"
  | "startingPrice";

export interface CompetitorRow {
  key: CompetitorRowKey;
  workloop: FeatureValue;
  jobber: FeatureValue;
  housecall: FeatureValue;
  servicetitan: FeatureValue;
}

export type PricingPlanId = "starter" | "professional" | "business";

export interface PricingPlan {
  id: PricingPlanId;
  icon: LucideIcon;
  price_monthly: number;
  price_annual: number;
  workers: number;
  extraTechPrice: number;
  /** Tailwind gradient classes for the plan icon badge (used by the /pricing page). */
  color: string;
  popular: boolean;
}

export const PLAN_FEATURES: PlanFeature[] = [
  { key: "workerApp",      starter: true,  professional: true,  business: true },
  { key: "customerApp",    starter: true,  professional: true,  business: true },
  { key: "invoicing",      starter: true,  professional: true,  business: true },
  { key: "scheduling",     starter: true,  professional: true,  business: true },
  { key: "gps",            starter: true,  professional: true,  business: true },
  { key: "routeHistory",   starter: false, professional: true,  business: true },
  { key: "equipment",      starter: false, professional: true,  business: true },
  { key: "googleCalendar", starter: false, professional: true,  business: true },
  { key: "performance",    starter: false, professional: true,  business: true },
];

export const COMPETITOR_FEATURES: CompetitorRow[] = [
  { key: "workerApp",     workloop: true,   jobber: "$$basic",       housecall: true,        servicetitan: true },
  { key: "customerApp",   workloop: true,   jobber: false,           housecall: false,       servicetitan: false },
  { key: "invoicing",     workloop: true,   jobber: true,            housecall: true,        servicetitan: true },
  { key: "realtimeGps",   workloop: true,   jobber: "$$connectPlus", housecall: "$$perVehicle", servicetitan: true },
  { key: "routeHistory",  workloop: true,   jobber: "$$proOnly",     housecall: "$$proOnly", servicetitan: true },
  { key: "bilingual",     workloop: true,   jobber: false,           housecall: false,       servicetitan: false },
  { key: "noContract",    workloop: true,   jobber: true,            housecall: true,        servicetitan: false },
  { key: "startingPrice", workloop: "$$startingWorkloop", jobber: "$39", housecall: "$59",    servicetitan: "$245+" },
];

// String values prefixed `$$` are translation-key tokens (resolved via
// landing.compare.values.*); anything else is a literal already-locale-safe
// price string.
export function isValueKey(v: FeatureValue): v is string {
  return typeof v === "string" && v.startsWith("$$");
}
export function valueKey(v: string): string {
  return v.slice(2);
}

export const FAQ_KEYS = ["trial", "cancel", "setup", "migration", "techLimit", "invoicing", "quickbooks", "customerApp", "dataExport", "support"] as const;
export type FaqKey = (typeof FAQ_KEYS)[number];

export const PLANS: PricingPlan[] = [
  { id: "starter",      icon: Zap,       price_monthly: 129, price_annual: 99,  workers: 2,  extraTechPrice: 19, color: "from-blue-500 to-cyan-500",   popular: false },
  { id: "professional", icon: Crown,     price_monthly: 289, price_annual: 229, workers: 8,  extraTechPrice: 15, color: "from-[#3525cd] to-[#7c6ef0]", popular: true  },
  { id: "business",     icon: Building2, price_monthly: 549, price_annual: 449, workers: 25, extraTechPrice: 12, color: "from-purple-500 to-pink-500", popular: false },
];
